import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_AllCombinators = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🔗 CSS Combinators (All Types)</h1>

      <p className="tutorial-intro">
        A <b>combinator</b> in CSS explains the relationship between selectors.
        It helps you target elements based on their position in the HTML
        structure.
      </p>

      {/* Highlight */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#1e3a8a", marginBottom: "0.5rem" }}>
          🌟 Why Learn CSS Combinators?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Combinators allow you to style elements without adding extra classes
          or IDs. They let you define rules based on hierarchy and sibling
          relationships.
        </p>
      </div>

      {/* 1. Descendant Selector */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Descendant Selector (space)</h2>
        <p>
          The <code>(space)</code> combinator selects all elements that are{" "}
          <b>inside</b> a specified element (at any nesting level).
        </p>

        <CodeCopyBlock
          code={`div p {
  color: blue;
}`}
        />

        <div
          style={{
            border: "2px solid #3b82f6",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <div>
            <p>This paragraph is inside a div (blue).</p>
          </div>
          <p>This paragraph is outside the div (normal).</p>
        </div>
      </div>

      {/* 2. Child Selector */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Child Selector (>)</h2>
        <p>
          The <code>&gt;</code> combinator selects elements that are{" "}
          <b>direct children</b> of a parent element.
        </p>

        <CodeCopyBlock
          code={`div > p {
  color: green;
}`}
        />

        <div
          style={{
            border: "2px solid #22c55e",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <p>This paragraph is a direct child (green).</p>
          <div>
            <p>This one is nested inside another div (normal).</p>
          </div>
        </div>
      </div>

      {/* 3. Adjacent Sibling Selector */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Adjacent Sibling Selector (+)</h2>
        <p>
          The <code>+</code> combinator selects the element that is{" "}
          <b>immediately after</b> another element.
        </p>

        <CodeCopyBlock
          code={`h2 + p {
  color: red;
}`}
        />

        <div
          style={{
            border: "2px solid #ef4444",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <h2>Heading</h2>
          <p>This paragraph is immediately after h2 (red).</p>
          <p>This one is not immediately after (normal).</p>
        </div>
      </div>

      {/* 4. General Sibling Selector */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ General Sibling Selector (~)</h2>
        <p>
          The <code>~</code> combinator selects <b>all siblings</b> that come
          after a specified element.
        </p>

        <CodeCopyBlock
          code={`h2 ~ p {
  color: purple;
}`}
        />

        <div
          style={{
            border: "2px solid #a855f7",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <h2>Heading</h2>
          <p>This paragraph is after h2 (purple).</p>
          <p>This one is also after h2 (purple).</p>
        </div>
      </div>

      {/* Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          margin: "2rem 0",
        }}
      >
        <h2 style={{ color: "#92400e" }}>📌 Quick Summary</h2>
        <ul style={{ lineHeight: "1.8" }}>
          <li>
            <b>Descendant ( )</b> → Selects elements inside another element.
          </li>
          <li>
            <b>Child (&gt;)</b> → Selects direct children only.
          </li>
          <li>
            <b>Adjacent Sibling (+)</b> → Selects the next immediate sibling.
          </li>
          <li>
            <b>General Sibling (~)</b> → Selects all siblings after an element.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know all combinators, you can combine them with{" "}
          <a href="/css/pseudo-classes" className="learn-more">
            pseudo-classes
          </a>{" "}
          and{" "}
          <a href="/css/pseudo-elements" className="learn-more">
            pseudo-elements
          </a>{" "}
          for advanced targeting.
        </p>
      </div>
    </div>
  );
};

export default CSS_AllCombinators;
